/**
 * Phase 21.4 Batch 3D — Public Contest listing page (/contests).
 *
 * Anonymous & authenticated users both see it. Feature-flag gated at
 * the API — when Contest Center is OFF, this page shows the "Coming
 * Soon" placeholder so the surface still exists but nobody can enter.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SharedNavigation from "@/components/SharedNavigation";
import ContestCard from "@/components/contests/ContestCard";
import api from "@/lib/api";
import { toast } from "sonner";
import { deriveContestDisplayStatus } from "@/lib/contestStatus";

const FILTER_KEYS = ["all", "open", "coming_soon", "judging", "closed"];
const FILTER_LABELS = {
  all: "All",
  open: "Open",
  coming_soon: "Coming Soon",
  judging: "Judging",
  closed: "Winners",
};

// iter_188 — Fix Winners tab counting to 0. When admins commit winners,
// `deriveContestDisplayStatus` returns `results_published`, NOT `closed`.
// The tab still reads "Winners" so it must include both.
const FILTER_STATUS_KEYS = {
  all: null,
  open: ["open"],
  coming_soon: ["coming_soon"],
  judging: ["judging", "submissions_closed"],
  closed: ["closed", "results_published"],
};

const ContestsPage = () => {
  const [enabled, setEnabled] = useState(null);
  const [contests, setContests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const isAuthed = typeof window !== "undefined" && !!localStorage.getItem("session_token");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/contests");
      if (r.data?.enabled === false) {
        setEnabled(false);
        setContests([]);
      } else {
        setEnabled(true);
        setContests(r.data?.contests || []);
      }
    } catch (e) {
      toast.error("Failed to load contests");
      setContests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (filter === "all") return contests;
    const keys = FILTER_STATUS_KEYS[filter] || [filter];
    return contests.filter((c) => keys.includes(deriveContestDisplayStatus(c).key));
  }, [contests, filter]);

  const counts = useMemo(() => {
    const c = { all: contests.length };
    for (const key of FILTER_KEYS.slice(1)) {
      const keys = FILTER_STATUS_KEYS[key] || [key];
      c[key] = contests.filter((x) => keys.includes(deriveContestDisplayStatus(x).key)).length;
    }
    return c;
  }, [contests]);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="contests-page">
      <SharedNavigation />

      <div className="max-w-6xl mx-auto px-4 pt-24 pb-16">
        {/* Hero */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] uppercase tracking-widest text-amber-300 font-black">🏆 RGM Studio</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight" data-testid="contests-page-title">
            Contest Center
          </h1>
          <p className="text-base text-gray-400 mt-3 max-w-2xl">
            Enter your tracks and beats in official RGM contests. Win reward coins, promo plays, and a spot in the Hall of Champions.
          </p>
          {isAuthed && (
            <div className="mt-4 flex flex-wrap gap-2">
              <Link
                to="/contests/my"
                className="inline-flex items-center px-4 py-2 rounded-full bg-white text-black text-sm font-black hover:bg-amber-200 transition-colors"
                data-testid="contests-page-my-btn"
              >
                My submissions
              </Link>
              <Link
                to="/hall-of-champions"
                className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/15 text-white text-sm font-black hover:bg-white/20 transition-colors"
                data-testid="contests-page-hof-btn"
              >
                🏛 Hall of Champions
              </Link>
            </div>
          )}
        </div>

        {/* Coming-soon empty state (flag off) */}
        {enabled === false && !loading && (
          <div
            className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-8 text-center"
            data-testid="contests-page-flag-off"
          >
            <p className="text-4xl mb-3">🏆</p>
            <h2 className="text-2xl font-black text-white mb-2">Contests are coming soon</h2>
            <p className="text-sm text-gray-400 max-w-md mx-auto">
              The RGM Contest Center is under construction. Follow @RGMStudio for launch announcements — you don&apos;t want to miss the first drop.
            </p>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <p className="text-sm text-gray-500" data-testid="contests-page-loading">Loading contests…</p>
        )}

        {/* Filters + grid */}
        {enabled && !loading && (
          <>
            <div className="flex flex-wrap gap-2 mb-6" data-testid="contests-page-filters">
              {FILTER_KEYS.map((k) => (
                <button
                  key={k}
                  onClick={() => setFilter(k)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-wider border transition-colors ${
                    filter === k
                      ? "bg-white text-black border-white"
                      : "bg-white/5 text-gray-300 border-white/10 hover:bg-white/10"
                  }`}
                  data-testid={`contests-page-filter-${k}`}
                >
                  {FILTER_LABELS[k]}
                  <span className="opacity-70">({counts[k] || 0})</span>
                </button>
              ))}
            </div>

            {filtered.length === 0 ? (
              <div
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-center"
                data-testid="contests-page-empty"
              >
                <p className="text-3xl mb-3">🎤</p>
                <p className="text-white font-black">No contests here right now</p>
                <p className="text-xs text-gray-400 mt-1">Check back soon — new contests drop regularly.</p>
              </div>
            ) : (
              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5"
                data-testid="contests-page-grid"
              >
                {filtered.map((c) => (
                  <ContestCard key={c.contest_id} contest={c} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ContestsPage;
